import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { generateId } from "./helpers";

// Engagement actions: reactions, pins, and thread-following. Spread into
// `actionsMutators` (actions.ts). Each enforces ownership/membership via
// `ctx.sub` on the server branch.

// ── addReaction core (D2.0-style extraction) ────────────────────────────────
// Shared between the client mutator (below) and the server-mutator override in
// `server/server-mutator-message-actions.ts`, which calls this core and then
// fires the reaction-notification producer. The schema is exported so the
// override re-uses the identical Zod shape via `defineMutator(...)`.
export const actionAddReactionSchema = z.object({
  messageId: z.string(),
  userId: z.string(),
  emoji: z.string(),
});

type AddReactionParams = {
  // Zero `Transaction` (client | server). Typed loosely to avoid importing the
  // Zero transaction union here; the client mutator + server override both pass
  // their real `tx`. Mirrors the `MutateSurface = any` convention used by the
  // message builders.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tx: any;
  args: z.infer<typeof actionAddReactionSchema>;
  ctx?: { sub?: string };
};

/**
 * Insert a reaction (idempotent on the migration-164 `(message_id, user_id,
 * emoji)` UNIQUE). Returns `true` when a NEW row was inserted, `false` on the
 * idempotent no-op (an identical reaction already exists). The boolean lets the
 * server override fire the reaction-notification producer ONLY on a fresh
 * reaction — a duplicate must not re-notify the message author.
 */
export async function addReactionCore({
  tx,
  args,
  ctx,
}: AddReactionParams): Promise<boolean> {
  if (ctx?.sub && args.userId !== ctx.sub) {
    throw new Error("Unauthorized: userId mismatch");
  }
  if (tx.location === "server") {
    const msg = await tx.run(zql.messages.where("id", args.messageId).one());
    if (!msg) throw new Error("Message not found");
    const membership = await tx.run(
      zql.channel_members
        .where("channel_id", msg.channel_id)
        .where("user_id", ctx?.sub)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!membership) {
      throw new Error("Unauthorized: not a channel member");
    }
  }
  // Idempotency across ALL clients (migration 164 added UNIQUE on
  // (message_id, user_id, emoji)). This action mutator is the write
  // behind POST /messages/:id/reactions (runActionMutator), used by
  // CLI/MCP/API. A duplicate (agent/CLI re-react, retried POST, stale
  // reactionList) would otherwise hit a 23505 UNIQUE violation and
  // surface as an unhandled 500. Look up an existing row for the same
  // identity and no-op instead — mirroring reactionsMutators.insert in
  // messages.ts. Runs UNCONDITIONALLY (not gated on tx.location) so the
  // optimistic apply and the authoritative replay agree. Not an upsert:
  // Zero's upsert arbiter is the PK `id` (a fresh generateId() that never
  // collides), so the triple UNIQUE would still fire.
  const existing = await tx.run(
    zql.reactions
      .where("message_id", args.messageId)
      .where("user_id", args.userId)
      .where("emoji", args.emoji)
      .one(),
  );
  if (existing) return false;
  await tx.mutate.reactions.insert({
    id: generateId(),
    message_id: args.messageId,
    user_id: args.userId,
    emoji: args.emoji,
    created_at: Date.now(),
  });
  return true;
}

export const engagementActionMutators = {
  // Add a reaction to a message. Thin client wrapper over `addReactionCore`;
  // the server override adds the reaction-notification producer on a fresh row.
  addReaction: defineMutator(actionAddReactionSchema, async (params) => {
    await addReactionCore(params);
  }),

  // Remove a reaction (by finding the reaction id)
  removeReaction: defineMutator(
    z.object({
      reactionId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const reaction = await tx.run(
          zql.reactions.where("id", args.reactionId).one(),
        );
        if (!reaction) return;
        if (reaction.user_id !== ctx.sub) {
          throw new Error("Unauthorized: can only remove own reactions");
        }
      }
      await tx.mutate.reactions.delete({ id: args.reactionId });
    },
  ),

  // Pin a message
  pinMessage: defineMutator(
    z.object({
      messageId: z.string(),
      channelId: z.string(),
      userId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.userId !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      if (tx.location === "server") {
        // Derive authoritative channel from message
        const msg = await tx.run(
          zql.messages.where("id", args.messageId).one(),
        );
        if (!msg) throw new Error("Message not found");
        if (msg.channel_id !== args.channelId) {
          throw new Error("Unauthorized: channelId mismatch with message");
        }
        const membership = await tx.run(
          zql.channel_members
            .where("channel_id", msg.channel_id)
            .where("user_id", ctx.sub)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (!membership) {
          throw new Error("Unauthorized: not a channel member");
        }
      }
      await tx.mutate.pins.insert({
        id: generateId(),
        message_id: args.messageId,
        channel_id: args.channelId,
        pinned_by: args.userId,
        pinned_at: Date.now(),
      });
    },
  ),

  // Follow a thread
  followThread: defineMutator(
    z.object({
      threadId: z.string(),
      userId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.userId !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      if (tx.location === "server") {
        const threadMsg = await tx.run(
          zql.messages.where("id", args.threadId).one(),
        );
        if (!threadMsg) throw new Error("Thread not found");
        const membership = await tx.run(
          zql.channel_members
            .where("channel_id", threadMsg.channel_id)
            .where("user_id", ctx.sub)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (!membership) {
          throw new Error("Unauthorized: not a channel member");
        }
      }
      await tx.mutate.thread_followers.insert({
        id: generateId(),
        thread_id: args.threadId,
        user_id: args.userId,
        followed_at: Date.now(),
      });
    },
  ),

  // Unfollow a thread
  unfollowThread: defineMutator(
    z.object({
      followId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const follow = await tx.run(
          zql.thread_followers.where("id", args.followId).one(),
        );
        if (!follow) return;
        if (follow.user_id !== ctx.sub) {
          throw new Error("Unauthorized: can only unfollow own threads");
        }
      }
      await tx.mutate.thread_followers.delete({ id: args.followId });
    },
  ),
};
